import React, { useEffect, useState, useCallback } from "react"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Badge } from "reactstrap"
import { get } from "helpers/api_helper"
import { SCANNING_BOOKINGS_URL } from "helpers/url_helper"
import moment from "moment"

const ScanningBookingsModal = ({ isOpen, toggle }) => {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [total, setTotal] = useState(0);

    const fetchBookings = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                booking_status: statusFilter,
                from_date: fromDate,
                to_date: toDate
            });
            const res = await get(SCANNING_BOOKINGS_URL + "?" + params.toString());
            if (res && res.status === 200) {
                const data = res.response?.data || [];
                setBookings(Array.isArray(data) ? data : []);
                setTotal(res.response?.total || (Array.isArray(data) ? data.length : 0));
            }
        } catch (e) {
            console.error("Failed to load doctor bookings", e);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, fromDate, toDate]);

    useEffect(() => {
        if (isOpen) {
            fetchBookings();
        }
    }, [isOpen, fetchBookings]);

    const getStatusBadge = (st) => {
        const s = String(st).toLowerCase();
        if (s === "1" || s === "accepted") {
            return <span className="badge rounded-pill bg-success px-2 py-1"><i className="bx bx-check me-1" />Accepted</span>;
        } else if (s === "2" || s === "cancelled") {
            return <span className="badge rounded-pill bg-danger px-2 py-1"><i className="bx bx-x me-1" />Cancelled</span>;
        }
        return <span className="badge rounded-pill bg-warning text-dark px-2 py-1"><i className="bx bx-time me-1" />Pending</span>;
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" centered>
            <ModalHeader toggle={toggle} style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                <div className="d-flex align-items-center gap-2">
                    <i className="bx bx-calendar-event text-primary" style={{ fontSize: "22px" }} />
                    <span className="fw-bold" style={{ fontSize: "16px", color: "#0f172a" }}>
                        Clinic Doctor Scan Bookings & Appointments
                    </span>
                    <Badge color="primary" pill className="ms-2">
                        Total: {total}
                    </Badge>
                </div>
            </ModalHeader>
            <ModalBody className="p-4" style={{ maxHeight: "72vh", overflowY: "auto" }}>
                {/* Filters */}
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 pb-3 border-bottom">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <select
                            className="form-select form-select-sm"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{ width: "160px", borderRadius: "8px" }}
                        >
                            <option value="">All Statuses</option>
                            <option value="0">Pending</option>
                            <option value="1">Accepted</option>
                            <option value="2">Cancelled</option>
                        </select>

                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            style={{ width: "140px", borderRadius: "8px" }}
                            title="From Date"
                        />
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            style={{ width: "140px", borderRadius: "8px" }}
                            title="To Date"
                        />

                        {(statusFilter || fromDate || toDate) && (
                            <button
                                className="btn btn-sm btn-light"
                                onClick={() => { setStatusFilter(""); setFromDate(""); setToDate(""); }}
                                style={{ borderRadius: "8px" }}
                            >
                                <i className="bx bx-x me-1" /> Clear
                            </button>
                        )}
                    </div>

                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={fetchBookings}
                        style={{ borderRadius: "8px" }}
                    >
                        <i className="bx bx-refresh me-1" /> Refresh
                    </button>
                </div>

                {/* Bookings Table */}
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead style={{ background: "#f8fafc", fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>
                            <tr>
                                <th style={{ width: "60px" }}>#</th>
                                <th>Doctor / Clinic</th>
                                <th>Patient Name</th>
                                <th>Requested Slot</th>
                                <th>Status</th>
                                <th>Notes / Instructions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-5">
                                        <i className="fas fa-spinner fa-spin text-primary fs-3" />
                                        <p className="mt-2 text-muted mb-0 small">Fetching scan bookings from portal...</p>
                                    </td>
                                </tr>
                            ) : bookings.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-5">
                                        <div className="avatar-md mx-auto mb-2 rounded-circle d-flex align-items-center justify-content-center bg-light">
                                            <i className="bx bx-calendar-x text-muted" style={{ fontSize: "28px" }} />
                                        </div>
                                        <h6 className="fw-bold text-muted mb-1">No Scan Bookings Found</h6>
                                        <p className="text-muted small mb-0">There are currently no active appointments registered for this date range.</p>
                                    </td>
                                </tr>
                            ) : (
                                bookings.map((b, idx) => (
                                    <tr key={b.id || idx}>
                                        <td className="fw-bold text-muted">{idx + 1}</td>
                                        <td>
                                            <div className="fw-bold text-dark">{b.doctor_name || b.client_name || "Doctor / Clinic"}</div>
                                            <div className="text-muted small">{b.clinic_name || b.client_email || ""}</div>
                                        </td>
                                        <td>
                                            <div className="fw-bold text-primary">{b.patient_name || "—"}</div>
                                            <div className="text-muted small">{b.teeth ? "Teeth: " + b.teeth : ""}</div>
                                        </td>
                                        <td>
                                            <div className="fw-semibold text-dark">
                                                {b.booking_slot ? moment(b.booking_slot).format("DD MMM YYYY, hh:mm A") : "—"}
                                            </div>
                                            <div className="text-muted small">
                                                {b.booking_slot ? moment(b.booking_slot).fromNow() : ""}
                                            </div>
                                        </td>
                                        <td>{getStatusBadge(b.booking_status)}</td>
                                        <td style={{ maxWidth: "250px" }}>
                                            <div className="text-truncate text-muted small" title={b.note || ""}>
                                                {b.note || "—"}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </ModalBody>
            <ModalFooter style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
                <Button color="secondary" onClick={toggle} style={{ borderRadius: "8px", fontSize: "13px" }}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default ScanningBookingsModal;
